import { Collapse } from '@chakra-ui/react';
import styled from '@emotion/styled';

import type { NavigationGutterItem } from '~/@types/redux';
import useLeftDrawerContext from '~/common/components/Nav/LeftDrawer/context/useLeftDrawerContext';
import { useNavSearchStore } from '~/stores/useNavSearchStore';

import ChildAlert from './ChildAlert';
import ParentAlert from './ParentAlert';
import { SEARCH_MIN_CHARACTERS } from './constants';

export const StyledAccordionItem = styled.div`
  border: 0;
  transition: all 0.5s ease-in-out;
`;

export const StyledAccordionPanel = styled.div`
  transition: all 0.5s ease-in-out;
  margin: 0;
  padding: 0;
`;

type AlertTabAccordionProps = {
  alert?: NavigationGutterItem;
  groupHeader?: string;
};

const StyledGroupHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-bottom: 1px solid ${(props) => props.theme.colors?.gray[200]};
  background-color: ${(props) => props.theme.colors?.gray[100]};
`;

const StyledGroupHeaderText = styled.h3`
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0px;
  line-height: 1.25rem;
  color: ${(props) => props.theme.colors?.gray[600]};
`;

const AlertTabAccordion = ({
  alert,
  groupHeader,
}: Readonly<AlertTabAccordionProps>) => {
  const { showParentChildren } = useLeftDrawerContext();
  const searchQuery = useNavSearchStore((s) => s.searchQuery);

  if (groupHeader) {
    return (
      <>
        <StyledGroupHeader data-testid={`group-header-${alert?.gutterItemId}`}>
          <StyledGroupHeaderText>{groupHeader}</StyledGroupHeaderText>
        </StyledGroupHeader>
        {alert?.gutterItemChildren?.map((child) => (
          <AlertTabAccordion key={child.gutterItemId} alert={child} />
        ))}
      </>
    );
  }
  const hasChildren = (alert?.gutterItemChildren?.length ?? 0) > 0;
  const tabKey = alert?.gutterItemId;
  const showChildAlert =
    searchQuery.trim().length >= SEARCH_MIN_CHARACTERS
      ? hasChildren
      : showParentChildren[tabKey];

  return (
    <StyledAccordionItem>
      <div role="tablist">
        <ParentAlert alert={alert} />
      </div>
      <StyledAccordionPanel role="tablist">
        <Collapse in={showChildAlert}>
          {alert?.gutterItemChildren?.map((childAlert) => (
            <ChildAlert
              parentAlert={alert}
              alert={childAlert}
              key={childAlert?.gutterItemId}
            />
          ))}
        </Collapse>
      </StyledAccordionPanel>
    </StyledAccordionItem>
  );
};

export default AlertTabAccordion;
